<template>
  <Dialog
    :visible="internalVisible"
    modal
    :header="t('event.karaoke.qrModalTitle')"
    :style="{ width: '450px' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    :pt="dialogPt"
    @update:visible="handleUpdateVisible"
  >
    <div class="p-fluid">
      <!-- Step 1: Name Input -->
      <div v-if="step === 'name-input'" class="space-y-4">
        <div
          v-if="isAlreadyQueued"
          class="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg"
          data-testid="karaoke-already-queued-warning"
        >
          <p class="text-sm text-teal-800 dark:text-teal-200 m-0">
            <i class="pi pi-exclamation-triangle mr-1 text-amber-600 dark:text-amber-400"></i>
            <strong>{{ t("event.karaoke.alreadyQueuedTitle") }}</strong>
            {{ t("event.karaoke.alreadyQueuedMessage") }}
          </p>
        </div>

        <!-- Song Info -->
        <div v-if="song">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ t("event.karaoke.selectedSong") }}
          </label>
          <div class="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <p class="font-medium text-gray-900 dark:text-white m-0 mb-1">
              {{ song.title }}
            </p>
            <p v-if="song.author" class="text-sm text-gray-600 dark:text-gray-400 m-0">
              by {{ song.author }}
            </p>
          </div>
        </div>

        <!-- Performer Name Field -->
        <div class="field">
          <InputText
            id="performer-name"
            v-model="performerName"
            :placeholder="t('event.karaoke.stageNamePlaceholder')"
            class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            :disabled="loading"
            autofocus
          />
          <small v-if="nameError" class="text-red-500 text-xs block mt-1">
            {{ nameError }}
          </small>
          <small class="text-gray-500 dark:text-gray-400 text-xs">
            {{ t("event.karaoke.stageNameMinLength") }}
          </small>
        </div>
      </div>

      <!-- Step 2: Show QR -->
      <div v-else-if="step === 'show-qr' && qrCodeDataUrl" class="space-y-4">
        <p
          v-if="expiresAtMs !== null"
          class="text-sm text-gray-900 dark:text-white m-0 font-bold text-center"
          data-testid="karaoke-qr-countdown"
        >
          <template v-if="remainingSeconds > 0">
            {{ t("event.karaoke.expiresCountdown", { time: formatCountdown(remainingSeconds) }) }}
          </template>
          <template v-else>
            {{ t("event.karaoke.expiresExpired") }}
          </template>
        </p>

        <!-- QR Code Display -->
        <div class="flex justify-center p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
          <img
            :src="qrCodeDataUrl"
            alt="Karaoke Signup QR"
            class="w-64 h-64"
          />
        </div>

        <!-- Token Info -->
        <div class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p class="text-sm text-blue-800 dark:text-blue-200 m-0 mb-2">
            <strong>{{ t("event.karaoke.songLabel") }}:</strong> {{ song?.title }}
          </p>
          <p class="text-sm text-blue-800 dark:text-blue-200 m-0">
            <strong>{{ t("event.karaoke.nameLabel") }}:</strong> {{ performerName }}
          </p>
        </div>

        <!-- Instructions -->
        <div class="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
          <p class="text-xs text-teal-800 dark:text-teal-200 leading-relaxed m-0">
            <i class="pi pi-info-circle mr-1 text-teal-600 dark:text-teal-400"></i>
            {{ t("event.karaoke.qrInstructions") }}
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        v-if="step === 'name-input'"
        :label="t('event.karaoke.prepareRegistration')"
        icon="pi pi-qrcode"
        severity="primary"
        class="w-full py-3 text-lg font-semibold"
        data-testid="karaoke-prepare-registration-button"
        @click="generateToken"
        :disabled="!canGenerate || loading"
      />
      <Button
        v-if="step === 'show-qr'"
        :label="t('event.karaoke.close')"
        severity="primary"
        class="w-full py-3 text-lg font-semibold"
        data-testid="karaoke-close-button"
        @click="closeModal"
      />
      <Button
        v-if="loading"
        :label="t('event.karaoke.processing')"
        icon="pi pi-spin pi-spinner"
        severity="primary"
        class="w-full py-3 text-lg font-semibold"
        disabled
      />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import QRCode from "qrcode";
import { useToast } from "primevue/usetoast";
import {
  buildKaraokeSignupQrPayload,
  karaokeSignupExpiresAt
} from "../utils/karaokeSignupQr";
import { formatCountdown } from "../utils/setlistCalculator";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  song: {
    type: Object,
    default: null
  },
  eventId: {
    type: Number,
    required: true
  },
  isAlreadyQueued: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:visible", "qr-generated"]);

const { t } = useI18n();
const toast = useToast();

const dialogPt = {
  root: {
    class:
      "karaoke-qr-modal bg-white dark:bg-gray-900 mx-4 sm:mx-0 rounded-xl overflow-hidden"
  },
  header: { class: "bg-white dark:bg-gray-900 dark:text-white font-heading" },
  content: { class: "bg-white dark:bg-gray-900 dark:text-gray-200" },
  footer: {
    class: "bg-white dark:bg-gray-900 w-full rounded-b-xl border-t-0"
  },
  closeButton: { class: "dark:hover:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-200" }
};

// State
const step = ref("name-input"); // 'name-input' | 'show-qr'
const performerName = ref("");
const nameError = ref("");
const loading = ref(false);
const token = ref("");
const expiresAtMs = ref(null);
const remainingSeconds = ref(0);
const qrCodeDataUrl = ref("");
let countdownTimer = null;

// Local state for dialog visibility
const internalVisible = ref(props.visible);

// Watchers
watch(() => props.visible, (newVal) => {
  internalVisible.value = newVal;
  if (newVal) {
    resetState();
  }
});

// Computed
const canGenerate = computed(() => {
  const trimmed = performerName.value.trim();
  return trimmed.length >= 2;
});

// Methods
const handleUpdateVisible = (value) => {
  if (!value) {
    closeModal();
    return;
  }
  internalVisible.value = value;
  emit("update:visible", value);
};

const stopCountdown = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
};

const updateRemainingSeconds = () => {
  if (expiresAtMs.value === null) {
    remainingSeconds.value = 0;
    return;
  }

  remainingSeconds.value = Math.max(
    0,
    Math.ceil((expiresAtMs.value - Date.now()) / 1000)
  );

  if (remainingSeconds.value <= 0) {
    stopCountdown();
  }
};

const startCountdown = (expiresAt) => {
  stopCountdown();
  expiresAtMs.value = expiresAt.getTime();
  updateRemainingSeconds();
  countdownTimer = setInterval(updateRemainingSeconds, 1000);
};

const resetState = () => {
  stopCountdown();
  step.value = "name-input";
  performerName.value = "";
  nameError.value = "";
  loading.value = false;
  token.value = "";
  expiresAtMs.value = null;
  remainingSeconds.value = 0;
  qrCodeDataUrl.value = "";
};

onUnmounted(stopCountdown);

const closeModal = () => {
  internalVisible.value = false;
  emit("update:visible", false);
  resetState();
};

const generateToken = async () => {
  const trimmedName = performerName.value.trim();
  
  // Validate
  if (trimmedName.length < 2) {
    nameError.value = t("event.karaoke.stageNameTooShort");
    return;
  }

  loading.value = true;
  nameError.value = "";

  try {
    const qrPayload = buildKaraokeSignupQrPayload({
      eventId: props.eventId,
      songId: props.song.id,
      performerName: trimmedName
    });
    const issuedAt = Math.floor(Date.now() / 1000);

    token.value = qrPayload;
    startCountdown(karaokeSignupExpiresAt(issuedAt));

    qrCodeDataUrl.value = await QRCode.toDataURL(qrPayload, {
      width: 256,
      margin: 2,
      color: {
        dark: "#000000",
        light: "#FFFFFF"
      }
    });

    step.value = "show-qr";
    emit("qr-generated", qrPayload);
  } catch (err) {
    console.error("Failed to generate signup QR:", err);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: t("event.karaoke.qrGeneratedError"),
      life: 5000
    });
  } finally {
    loading.value = false;
  }
};
</script>

<style>
.karaoke-qr-modal.p-dialog .p-dialog-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.karaoke-qr-modal.p-dialog .p-dialog-content {
  max-height: 70vh;
  overflow-y: auto;
}

.karaoke-qr-modal.p-dialog .p-dialog-footer {
  display: flex;
  width: 100%;
  padding-top: 0;
}

.karaoke-qr-modal.p-dialog .p-dialog-footer .p-button {
  flex: 1 1 100%;
  width: 100%;
  justify-content: center;
}
</style>
